/**
 * What TeX-driven math output may carry: the one policy every SciFlow MathJax surface applies.
 *
 * TeX is author input. `\href{javascript:...}{x}`, `\mmlToken{mi}[href="..."]`, `\class`, `\cssId`,
 * `\style`, `\bbox[...]`, `\data` and friends put attributes on the MathML tree that become live links,
 * layout takeovers or arbitrary markup in the SVG / MathML / HTML we emit. This file decides, per
 * attribute, what survives. It is deliberately pure and import-free (no MathJax, no DOM) so the same bytes
 * run in the export engines, the legacy server renderers and the browser editor; the copies in the SciFlow
 * application are kept identical to this file by a check that runs in CI. Do not edit a copy, edit this file.
 *
 * Apply it by registering {@link sanitizeMathRoot} as a post-filter on the TeX input jax:
 *
 *     tex.postFilters.add((data) => { data.math.root = tex.parseOptions.root; sanitizeMathRoot(data.math.root); }, -5.5);
 *
 * Rules:
 * - URLs (`href`, `src`, `altimg`, `xlink:href`): http, https, mailto, ftp, doi, or a scheme-less/relative
 *   reference. Whitespace, control and zero-width characters are ignored when reading the scheme, because
 *   browsers ignore them too (`java<TAB>script:`).
 * - `class`: only MathJax's own names (`mjx-*`, `MJX-*`, `MathJax_*`); author classes are removed. Author `id`: removed. MathJax's own tag ids (`mjx-eqn:...` on a table cell) are kept; an
 *   author's `\cssId{mjx-eqn:1}` on any other node is refused.
 * - `style`: MathJax's safe list (colour, background, border, margin, padding, outline, text-shadow, font
 *   family/size/style/weight, opacity) without `cursor`. Any value with `url(`, `expression(`, `calc(`,
 *   `var(`, `attr(`, `image-set(`, `cross-fade(`, `image(`, `element(`, `javascript:`, an escape (`\`) or a comment
 *   is refused (whitespace inside the function name is ignored). Font sizes are clamped to
 *   0.1-10em and margins/paddings to +-3em. Allowed declarations keep the author's own spelling.
 * - `mathsize`, `fontsize`, `scriptminsize`: clamped to 0.1-10em. `scriptlevel` is clamped to [-2, 2] and
 *   `scriptsizemultiplier` to [0.6, 1] (numbers stay numbers). `mathcolor`, `mathbackground`, `color`, `background` must be
 *   plain colour syntax (name, #hex, rgb()/hsl()).
 * - Lengths in `border*`, `outline*`, `text-shadow` and `box-shadow` are capped at +-3em like margins and paddings.
 * - `data-*`: only the names MathJax's own TeX-to-MathML pipeline writes (`data-mjx-*`, `data-latex*`, `data-break-align`,
 *   `data-vertical-align`, `data-frame-styles`, ... listed in {@link MATHJAX_DATA_ATTRIBUTES}); an author's `\data{k=v}` or
 *   `\mmlToken{mi}[data-k=v]` is removed. Those names are inert text, so an author reusing one is harmless. `on*` handlers are removed.
 */

export const SAFE_URL_SCHEMES = ['http', 'https', 'mailto', 'ftp', 'doi'] as const;

const SAFE_STYLE_PROPERTIES = new Set<string>([
  'color',
  'background',
  'background-color',
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-width',
  'border-style',
  'border-color',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'outline',
  'text-shadow',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'opacity',
]);

const MIN_FONT_EM = 0.1;
const MAX_FONT_EM = 10;
const MAX_BOX_EM = 3;

/** Characters a browser drops before it reads a URL scheme: controls, spaces, soft hyphen, zero-width and line separators, BOM. */
const isIgnoredByUrlParsers = (c: number): boolean =>
  c <= 0x20 ||
  (c >= 0x7f && c <= 0x9f) ||
  c === 0xad ||
  (c >= 0x200b && c <= 0x200f) ||
  c === 0x2028 ||
  c === 0x2029 ||
  c === 0x2060 ||
  c === 0xfeff;

const compactUrl = (url: string): string => {
  let out = '';
  for (let i = 0; i < url.length; i++) {
    if (!isIgnoredByUrlParsers(url.charCodeAt(i))) {
      out += url[i];
    }
  }
  return out;
};

const NAMED_REFERENCES: Record<string, string> = {
  colon: ':', Tab: '\t', NewLine: '\n', amp: '&', AMP: '&', lt: '<', gt: '>', quot: '"', apos: "'", sol: '/', bsol: '\\',
  lpar: '(', rpar: ')', period: '.', comma: ',', semi: ';', num: '#', excl: '!', quest: '?', equals: '=', plus: '+',
  hyphen: '-', dash: '-', lowbar: '_', lsqb: '[', rsqb: ']', lbrack: '[', rbrack: ']', lcub: '{', rcub: '}', commat: '@',
  percnt: '%', dollar: '$', ast: '*', midast: '*', vert: '|', verbar: '|', grave: '`', Hat: '^', nbsp: ' ',
};

/** Decode HTML character references the way a browser does in an attribute value (numeric with or without `;`, common named ones). */
const decodeReferences = (text: string): string =>
  text.replace(/&(?:#[xX]([0-9a-fA-F]+)|#([0-9]+)|([A-Za-z][A-Za-z0-9]*));?/g, (whole, hex, dec, name) => {
    if (hex || dec) {
      const code = hex ? parseInt(hex, 16) : parseInt(dec, 10);
      return Number.isFinite(code) && code >= 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
    }
    return Object.prototype.hasOwnProperty.call(NAMED_REFERENCES, name) ? NAMED_REFERENCES[name] : whole;
  });

/** Whether a URL found in author TeX may be kept. Character references are decoded first, because the output is parsed as HTML later. */
export const isSafeMathUrl = (url: unknown): boolean => {
  let text = String(url ?? '');
  // decode until stable (`&amp;#106;`), so a double-encoded scheme is judged in its final form
  for (let i = 0; i < 4; i++) {
    const next = decodeReferences(text);
    if (next === text) break;
    text = next;
  }
  const compact = compactUrl(text);
  if (compact === '') {
    return false;
  }
  const scheme = compact.match(/^([^:/?#]*):/);
  if (!scheme) {
    return true; // a relative reference or a fragment
  }
  return (SAFE_URL_SCHEMES as readonly string[]).includes(scheme[1].toLowerCase());
};

const UNIT_IN_EM: Record<string, number> = {
  em: 1,
  rem: 1,
  ex: 0.5,
  ch: 0.5,
  px: 1 / 16,
  pt: 1 / 12,
  pc: 1,
  mm: 96 / 25.4 / 16,
  cm: 96 / 2.54 / 16,
  in: 6,
  '%': 0.01,
};

const LENGTH = /^(-?(?:\d+\.?\d*|\.\d+))(em|rem|ex|ch|px|pt|pc|mm|cm|in|%)?$/i;

const formatEm = (em: number): string => `${Number(em.toFixed(3))}em`;

/**
 * Clamp one length to [min, max] em. Fails closed: a value that is not a plain number with a known unit
 * (exponent notation, viewport and container units such as `vh`, `vmax`, `cqw`, anything else) returns `null`.
 * A bare number is only accepted when `bare` is set (`mathsize="2"` is a multiplier) or when it is zero.
 */
const clampLength = (value: string, min: number, max: number, bare = false): string | null => {
  const m = value.trim().match(LENGTH);
  if (!m) {
    return null;
  }
  const unit = (m[2] ?? '').toLowerCase();
  if (unit === '') {
    if (Number(m[1]) === 0) {
      return value;
    }
    if (!bare) {
      return null;
    }
    const n = Number(m[1]);
    return n < min ? String(min) : n > max ? String(max) : value;
  }
  const em = Number(m[1]) * (UNIT_IN_EM[unit] ?? 1);
  if (!Number.isFinite(em)) {
    return null;
  }
  return em < min ? formatEm(min) : em > max ? formatEm(max) : value;
};

const SIZE_KEYWORD = /^[a-z]+(-[a-z]+)*$/i;

/** Font size / `mathsize`: a length (clamped to 0.1-10em), a bare multiplier, or a keyword (`small`, `big`, `larger`...). Anything else: null. */
const clampFontSize = (value: string): string | null => {
  const text = value.trim();
  if (SIZE_KEYWORD.test(text)) {
    return value;
  }
  return clampLength(text, MIN_FONT_EM, MAX_FONT_EM, true);
};

const BOX_KEYWORD = /^(auto|inherit|initial|unset)$/i;

const clampBox = (value: string): string | null => {
  const parts: string[] = [];
  for (const part of value.trim().split(/\s+/)) {
    if (BOX_KEYWORD.test(part)) {
      parts.push(part);
      continue;
    }
    const clamped = clampLength(part, -MAX_BOX_EM, MAX_BOX_EM);
    if (clamped === null) {
      return null;
    }
    parts.push(clamped);
  }
  return parts.join(' ');
};

const FORBIDDEN_FUNCTIONS = /(url|expression|calc|var|attr|env|image-set|-webkit-image-set|cross-fade|-webkit-cross-fade|image|element|-moz-element|paint|src)\(/;
const FORBIDDEN_TEXT = /javascript:|vbscript:|data:|@import|\\|\/\*|<|>|&|!/i;

/** True when a CSS value contains a function that can fetch or compute something, whatever the spacing or case. */
const hasForbiddenContent = (value: string): boolean => {
  let compact = '';
  for (let i = 0; i < value.length; i++) {
    if (!isIgnoredByUrlParsers(value.charCodeAt(i))) compact += value[i];
  }
  return FORBIDDEN_FUNCTIONS.test(compact.toLowerCase()) || FORBIDDEN_TEXT.test(value);
};

/** Plain colour syntax: a name, #hex, or rgb()/rgba()/hsl()/hsla() with numbers. */
const isColourValue = (value: string): boolean =>
  /^\s*(#[0-9a-f]{3,8}|[a-z]+|(?:rgb|hsl)a?\([0-9a-z.,%\s/+-]*\))\s*$/i.test(value);

const NUMBER_OR_HEX = /#[0-9a-fA-F]+|(-?)(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?([a-zA-Z%]*)/g;
const HARMLESS_UNITS = new Set(['deg', 'rad', 'grad', 'turn', '%']);

/**
 * Cap every length inside a compound value (a border, an outline, a shadow) at +-3em. EVERY number is looked at,
 * whatever surrounds it (`100vmax#000`), except the digits of a hex colour. Fails closed: exponent notation or an
 * unknown unit (`vh`, `vmax`, `cqw`, ...) makes the whole declaration `null`.
 */
const clampCompoundLengths = (value: string): string | null => {
  let refused = false;
  const out = value.replace(NUMBER_OR_HEX, (whole, sign: string | undefined, num: string | undefined, exp: string | undefined, unit: string | undefined) => {
    if (whole[0] === '#') {
      return whole;
    }
    if (exp) {
      refused = true;
      return whole;
    }
    const u = (unit ?? '').toLowerCase();
    if (u === '' || HARMLESS_UNITS.has(u)) {
      return whole;
    }
    if (!Object.prototype.hasOwnProperty.call(UNIT_IN_EM, u)) {
      refused = true;
      return whole;
    }
    const em = Number(num) * UNIT_IN_EM[u];
    if (!Number.isFinite(em)) {
      refused = true;
      return whole;
    }
    return em <= MAX_BOX_EM ? whole : formatEm(sign ? -MAX_BOX_EM : MAX_BOX_EM);
  });
  return refused ? null : out;
};

/** Split a style text on `;` that are not inside quotes or parentheses. */
const splitDeclarations = (style: string): string[] => {
  const out: string[] = [];
  let depth = 0;
  let quote = '';
  let current = '';
  for (const ch of style) {
    if (quote) {
      if (ch === quote) quote = '';
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '(') {
      depth++;
    } else if (ch === ')') {
      depth = Math.max(0, depth - 1);
    } else if (ch === ';' && depth === 0) {
      out.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  out.push(current);
  return out;
};

/** The safe part of a `style` attribute, or `null` when nothing is left. */
export const filterMathStyle = (style: unknown): string | null => {
  const kept: string[] = [];
  for (const declaration of splitDeclarations(String(style ?? ''))) {
    const colon = declaration.indexOf(':');
    if (colon < 1) {
      continue;
    }
    const property = declaration.slice(0, colon).trim().toLowerCase();
    let value = declaration.slice(colon + 1).trim();
    if (!SAFE_STYLE_PROPERTIES.has(property) || value === '' || hasForbiddenContent(value)) {
      continue;
    }
    if (property === 'font-size') {
      const sized = clampFontSize(value);
      if (sized === null) continue;
      value = sized;
    } else if (/^(margin|padding)(-|$)/.test(property)) {
      const boxed = clampBox(value);
      if (boxed === null) continue;
      value = boxed;
    } else if (/^(border|outline)(-|$)|^(text|box)-shadow$/.test(property)) {
      const compound = clampCompoundLengths(value);
      if (compound === null) continue;
      value = compound;
    }
    if (/^(color|background-color|border-color)$/.test(property) && !isColourValue(value)) {
      continue;
    }
    kept.push(`${declaration.slice(0, colon).trim()}:${value}`);
  }
  return kept.length ? kept.join(';') : null;
};

/** `mathsize` / `fontsize` / `scriptminsize`: keywords stay, lengths are clamped to 0.1-10em. */
export const filterMathSize = (value: unknown): string | null => {
  const text = String(value ?? '').trim();
  if (hasForbiddenContent(text)) {
    return null;
  }
  return clampFontSize(text);
};

/** `data-*` attributes MathJax 3 and 4 write themselves (found by searching their TeX input, core and output sources). */
export const MATHJAX_DATA_ATTRIBUTES: ReadonlySet<string> = new Set([
  'data-align',
  'data-array-padding',
  'data-arrowhead',
  'data-braketbar',
  'data-break-align',
  'data-cramped',
  'data-frame-styles',
  'data-lineleading',
  'data-offsets',
  'data-overflow',
  'data-padding',
  'data-thickness',
  'data-vertical-align',
  'data-width-includes-label',
]);

const isMathJaxDataAttribute = (key: string): boolean =>
  /^data-(mjx-|latex|semantic-)/.test(key) || MATHJAX_DATA_ATTRIBUTES.has(key);

/** Class names MathJax writes itself (`mjx-*` in 4, `MJX-*` and `MathJax_*` in 2 and 3, for example `MathJax_ref` on an `\\eqref`). */
const OWN_CLASS = /^(mjx-|MJX-|MathJax_)[-a-zA-Z0-9_.]*$/;

/** Clamp a number (or numeric string) into [min, max]; a number stays a number, an in-range value is returned as it was. */
const clampNumber = (value: unknown, min: number, max: number): unknown => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  if (!Number.isFinite(n)) {
    return null;
  }
  if (n >= min && n <= max) {
    return value;
  }
  const clamped = Math.min(max, Math.max(min, n));
  return typeof value === 'number' ? clamped : String(clamped);
};

/** MathJax writes the id of an equation tag's table cell itself (`mjx-eqn:1`); nothing else may carry one. */
const isOwnMathId = (id: string, kind: string | undefined): boolean =>
  (kind === 'mtd' || kind === 'mlabeledtr') && /^mjx-eqn:[-a-zA-Z0-9_.:]+$/.test(id);

/**
 * Decide one attribute of a MathML node. Returns the value to keep (possibly rewritten) or `null` to remove it.
 * `kind` is the node's MathML kind (`mi`, `mtd`, ...), used for the one id MathJax writes itself.
 */
export const filterMathAttribute = (name: string, value: unknown, kind?: string): unknown => {
  const key = name.toLowerCase();
  if (key.startsWith('on')) {
    return null;
  }
  if (key.startsWith('data-')) {
    return isMathJaxDataAttribute(key) ? value : null;
  }
  switch (key) {
    case 'href':
    case 'src':
    case 'altimg':
    case 'xlink:href':
      return isSafeMathUrl(value) ? value : null;
    case 'class': {
      const kept = String(value ?? '').split(/\s+/).filter((name) => OWN_CLASS.test(name));
      return kept.length ? kept.join(' ') : null;
    }
    case 'id':
      return isOwnMathId(String(value ?? ''), kind) ? value : null;
    case 'style':
      return filterMathStyle(value);
    case 'scriptlevel':
      return clampNumber(value, -2, 2);
    case 'scriptsizemultiplier':
      return clampNumber(value, 0.6, 1);
    case 'mathcolor':
    case 'mathbackground':
    case 'color':
    case 'background':
      return isColourValue(String(value ?? '')) ? value : null;
    case 'mathsize':
    case 'fontsize':
    case 'scriptminsize': {
      if (typeof value === 'number') {
        return value;
      }
      return filterMathSize(value);
    }
    default:
      return value;
  }
};

/** The slice of a MathJax MML node the policy needs (structural, so v3 and v4 both fit). */
type MathNodeLike = {
  kind?: string;
  attributes?: { getAllAttributes(): Record<string, unknown> };
  walkTree?(fn: (node: MathNodeLike, data?: unknown) => void, data?: unknown): unknown;
};

/** Filter every attribute of every node under `root`, in place. */
export const sanitizeMathRoot = (root: MathNodeLike | null | undefined): void => {
  if (!root || typeof root.walkTree !== 'function') {
    return;
  }
  root.walkTree((node) => {
    const attributes = node.attributes?.getAllAttributes();
    if (!attributes) {
      return;
    }
    for (const name of Object.keys(attributes)) {
      const original = attributes[name];
      const next = filterMathAttribute(name, original, node.kind);
      if (next === null || next === undefined) {
        delete attributes[name];
      } else if (next !== original) {
        attributes[name] = next;
      }
    }
  });
};
